import { useRouter } from 'next/navigation';
import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { GetResourcesGrantingAccess } from '@frameio/graphql-client/graphql/features/invites/queries/generated/GetResourcesGrantingAccess';
import { isPermissionDeniedError } from '@frameio/next-shared/utils/errorHelpers';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import { getLink } from '@frameio/shared/routing';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import type { handleMembershipDeletionArgs } from '../components/InviteAccessMembers/types';
import { ProjectArchiveStatus } from '../gql/generated/ProjectArchiveStatus';
import TEXT from './generated/TEXT/useDeleteWithAccessCheck';
import type { DeleteMembershipMutationArgs } from './useDeleteMembership';
import { useDeleteMembership } from './useDeleteMembership';
import { useResourceName } from './useResourceName';
import { useRestoreMembership } from './useRestoreMembership';

TEXT.DEFINE({
  /** Label for undo button in toast notification */
  undoLabel: 'Undo',
});

type DeleteWithAccessCheckArgs = Omit<
  DeleteMembershipMutationArgs,
  'resourceName'
> & { resourceType: string };

// Handles membership deletion with access check logic
export const useDeleteWithAccessCheck = ({
  onBeginMutation,
  onCompleteMutation,
  resourceId,
  resourceType,
}: DeleteWithAccessCheckArgs) => {
  const router = useRouter();
  const client = useApolloClient();
  const activeUserId = useGetActiveUserId();
  const accountId = useActiveAccountId();
  const resourceName = useResourceName(resourceId, resourceType);

  // Handles cache cleanup after a user loses access to a resource
  const handleCacheEviction = (cache: ApolloCache<any>) => {
    cache.evict({
      id: cache.identify({ __typename: 'Project', id: resourceId }),
    });

    // Check if the project is archived
    const projectData = cache.readFragment({
      id: cache.identify({ __typename: 'Project', id: resourceId }),
      fragment: ProjectArchiveStatus,
      fragmentName: 'ProjectArchiveStatus',
    });

    const isActiveProject = !projectData?.archivedAt;

    // Update project counts in the account cache
    cache.modify({
      id: cache.identify({ __typename: 'Account', id: accountId }),
      fields: {
        fullAccessActiveProjectCount(existing = 0) {
          return isActiveProject ? Math.max(0, existing - 1) : existing;
        },
        fullAccessInactiveProjectCount(existing = 0) {
          return !isActiveProject ? Math.max(0, existing - 1) : existing;
        },
      },
    });

    cache.gc();
  };

  // Set up membership deletion with special handling for self-removal
  const { deleteMembership, deletedMembershipIndex } = useDeleteMembership({
    onBeginMutation,
    onCompleteMutation,
    resourceId,
    resourceName: resourceName ?? '',
    onUpdateCache: async (cache, data) => {
      const deletedUserId =
        data?.deleteMembership?.membership?.accountUser?.userId;
      const isSelfRemoval = deletedUserId === activeUserId;

      if (isSelfRemoval && accountId) {
        // If user removes themselves, check if they still have access to the resrouce
        try {
          await client.query({
            query: GetResourcesGrantingAccess,
            variables: {
              accountId,
              id: resourceId,
              page: { first: DEFAULT_INITIAL_PAGE_SIZE },
            },
            fetchPolicy: 'network-only',
          });
          return undefined;
        } catch (error) {
          // If user fon't have access, clean up cache and redirect to workspaces
          if (isPermissionDeniedError(error)) {
            handleCacheEviction(cache);
            router.push(getLink({ page: 'workspaces' }));
            return { hideUndoButton: true };
          }
        }
      }
      return undefined;
    },
  });

  const restoreMembership = useRestoreMembership({
    onBeginMutation: (id) => onBeginMutation?.('undoDelete', id),
    onCompleteMutation: (id) => onCompleteMutation?.(id),
    resourceId,
    resourceName: resourceName ?? '',
    membershipIndex: deletedMembershipIndex.current,
  });

  return ({
    membershipId,
    memberName,
    refetchQueries,
    onUndo,
  }: handleMembershipDeletionArgs) =>
    deleteMembership({
      membershipId,
      memberName,
      refetchQueries,
      successCta:
        typeof onUndo === 'function'
          ? {
              label: TEXT.undoLabel,
              callback: (id) => {
                restoreMembership(id, refetchQueries);
                onUndo?.();
              },
            }
          : undefined,
    });
};
